/**
 * The sales screen's slice of the API, in the shape of `../api.ts`. The
 * contract is docs/integrations.md.
 *
 * Money crossing this boundary is integer cents, signed — a refund is a sale
 * with the sign turned over. The screen turns it into dollars once, where it
 * is drawn.
 */

import type { PosDay, PosSyncState, ReportKind, ReportRecord } from "@baros/core";
import { request as shared } from "../api.js";

async function request<T>(token: string, method: "GET" | "POST", path: string): Promise<T> {
  // The shared client: it refreshes an expired token once and ends
  // the session when it cannot (see `request` in src/api.ts).
  return shared<T>(token, method, path);
}

const pos = (venueId: string) => `/v1/venues/${encodeURIComponent(venueId)}/pos`;
const venue = (venueId: string) => `/v1/venues/${encodeURIComponent(venueId)}`;

export interface PosWindow {
  readonly from: string;
  readonly to: string;
}

/** A venue's nights at the till, and whether there is a till to read at all. */
export interface PosDays {
  /** The organisation has connected Square. */
  readonly connected: boolean;
  /** This venue is linked to one of the merchant's locations. */
  readonly linked: boolean;
  readonly days: readonly PosDay[];
  readonly window: PosWindow;
  /** Null until the venue's first sync has run. */
  readonly sync: PosSyncState | null;
}

/**
 * The written reports whose period starts inside a span, by kind (#169).
 *
 * Grouped by kind because the screen wants different things from each: the
 * week's or the month's read for the period on show, and the nights inside
 * it beside each night's figures.
 */
export type Reports = { readonly [K in ReportKind]: readonly ReportRecord[] };

export const salesApi = {
  /** Omit the bounds for the default window, the last 14 service dates. */
  days: (token: string, venueId: string, range?: PosWindow) => {
    const query = range ? `?${new URLSearchParams({ from: range.from, to: range.to })}` : "";
    return request<PosDays>(token, "GET", `${pos(venueId)}/days${query}`);
  },

  /**
   * Read Square now rather than at the next quarter-hour. A 409 means the
   * venue is not linked. Only `sync` is read from the answer — the screen
   * refetches the nights rather than trust a second shape for them.
   */
  sync: (token: string, venueId: string) =>
    request<{ sync: PosSyncState; complete?: boolean }>(token, "POST", `${pos(venueId)}/sync`),

  /**
   * The reports for a span. Nothing is generated on request — the schedules
   * write them after a period closes — so an empty answer means the period
   * has not closed yet, or the organisation has not turned reports on.
   */
  reports: (token: string, venueId: string, range: PosWindow) => {
    const query = new URLSearchParams({ from: range.from, to: range.to });
    return request<{ reports: Partial<Reports> }>(token, "GET", `${venue(venueId)}/reports?${query}`);
  },
};
